/**
 * Interface for expense and income storage services
 * Implementations: LocalStorageExpenseService, FirebaseExpenseService
 */
export class IExpenseStorageService {
  /**
   * Returns all expenses for the given month, including recurring expenses
   * that started on or before that month (projected to the same
   * day-of-month within the requested month).
   * @param {number} year
   * @param {number} month - 1-12
   * @returns {Promise<import('../models/ExpenseModels').Expense[]>}
   */
  async getExpensesForMonth(year, month) {
    throw new Error('Method not implemented');
  }

  /**
   * Returns every expense that falls in the given year, including one
   * recurring-expense instance per month it occurs in (so a twice-a-month
   * recurring expense contributes up to 24 instances for a full year).
   * @param {number} year
   * @returns {Promise<import('../models/ExpenseModels').Expense[]>}
   */
  async getExpensesForYear(year) {
    throw new Error('Method not implemented');
  }

  /**
   * Persists a new expense.
   * @param {Omit<import('../models/ExpenseModels').Expense, 'id'>} data
   * @returns {Promise<import('../models/ExpenseModels').Expense>}
   */
  async addExpense(data) {
    throw new Error('Method not implemented');
  }

  /**
   * Replaces an existing expense by id.
   * @param {import('../models/ExpenseModels').Expense} expense
   * @returns {Promise<import('../models/ExpenseModels').Expense>}
   */
  async updateExpense(expense) {
    throw new Error('Method not implemented');
  }

  /**
   * Removes an expense by id.
   * @param {string} id
   * @returns {Promise<void>}
   */
  async deleteExpense(id) {
    throw new Error('Method not implemented');
  }

  /**
   * Returns the income set for the given month (0 if not set).
   * @param {number} year
   * @param {number} month
   * @returns {Promise<number>}
   */
  async getIncome(year, month) {
    throw new Error('Method not implemented');
  }

  /**
   * Persists the income for the given month.
   * @param {number} year
   * @param {number} month
   * @param {number} amount
   * @returns {Promise<void>}
   */
  async setIncome(year, month, amount) {
    throw new Error('Method not implemented');
  }

  /**
   * Returns a snapshot of all expenses and income data for export.
   * @returns {Promise<{expenses: import('../models/ExpenseModels').Expense[], income: Record<string, number>}>}
   */
  async exportData() {
    throw new Error('Method not implemented');
  }

  /**
   * Replaces all expenses and income data with the provided snapshot.
   * @param {{expenses: import('../models/ExpenseModels').Expense[], income: Record<string, number>}} data
   * @returns {Promise<void>}
   */
  async importData(data) {
    throw new Error('Method not implemented');
  }
}
